import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, children, disabled, ...props }, ref) => {
    const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-150 ease-out disabled:opacity-50 disabled:cursor-not-allowed';
    
    const variants = {
      primary: 'bg-foreground text-background hover:bg-white hover:-translate-y-0.5 shadow-[0_4px_12px_rgba(255,255,255,0.1)] hover:shadow-[0_4px_12px_rgba(255,255,255,0.2)]',
      secondary: 'bg-transparent text-foreground border border-border hover:border-accent hover:text-accent',
      accent: 'bg-gradient-to-r from-accent to-accent-secondary text-background font-semibold hover:-translate-y-0.5',
      ghost: 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-5 py-2.5 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

const buttonVariants = ({ variant = 'primary', size = 'md' }: { variant?: ButtonProps['variant']; size?: ButtonProps['size'] } = {}) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-150 ease-out disabled:opacity-50 disabled:cursor-not-allowed';
  const variants: Record<string, string> = {
    primary: 'bg-foreground text-background hover:bg-white hover:-translate-y-0.5',
    secondary: 'bg-transparent text-foreground border border-border hover:border-accent hover:text-accent',
    accent: 'bg-gradient-to-r from-accent to-accent-secondary text-background font-semibold hover:-translate-y-0.5',
    ghost: 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted',
    outline: 'bg-transparent text-foreground border border-border hover:border-accent hover:text-accent',
    default: 'bg-foreground text-background hover:bg-white',
  };
  const sizes: Record<string, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-5 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg',
    icon: 'h-10 w-10',
    default: 'px-5 py-2.5 text-base',
  };
  return `${baseStyles} ${variants[variant || 'primary'] || variants.primary} ${sizes[size || 'md'] || sizes.md}`;
};

export { Button, buttonVariants };
